import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useSetPageHeader } from '../../components/app/AppShell';
import { formatAadhaar } from '../../lib/aadhaar';
import { haptic } from '../../lib/haptic';

type DocKey = 'dl' | 'aadhaar' | 'pan';

function formatDateLong(ymd: string | null): string | null {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function KycIndexRoute() {
  const { t } = useTranslation('profile');
  useSetPageHeader({ backTo: '/profile', title: t('kyc.indexTitle') }, []);
  const navigate = useNavigate();
  const driver = useAuth(s => s.driver);

  if (!driver) {
    return <div className="px-5 pt-2 pb-10" />;
  }

  const rows: { key: DocKey; title: string; uploaded: boolean; preview: string }[] = [
    {
      key: 'dl',
      title: t('kyc.dlTitle'),
      uploaded: driver.hasDl,
      preview: driver.drivingLicenseNumber
        ? driver.drivingLicenseExpiry
          ? `${driver.drivingLicenseNumber} · ${t('kyc.dlExpiresOn', { date: formatDateLong(driver.drivingLicenseExpiry) })}`
          : driver.drivingLicenseNumber
        : t('kyc.notOnFile'),
    },
    {
      key: 'aadhaar',
      title: t('kyc.aadhaarTitle'),
      uploaded: driver.hasAadhaar,
      preview: driver.aadhaarNumber ? formatAadhaar(driver.aadhaarNumber) : t('kyc.notOnFile'),
    },
    {
      key: 'pan',
      title: t('kyc.panTitle'),
      uploaded: driver.hasPan,
      preview: driver.panNumber ?? t('kyc.notOnFile'),
    },
  ];

  return (
    <div className="px-5 pt-2 pb-10">
      <div className="space-y-2">
        {rows.map(row => (
          <button
            key={row.key}
            type="button"
            onClick={() => { haptic.tap(); navigate(`/profile/kyc/${row.key}`); }}
            className="w-full text-left bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[14px] p-4 flex items-center gap-3"
            style={{ minHeight: 'var(--touch-target, 52px)' }}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold text-[var(--color-text)]">{row.title}</span>
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
                  style={
                    row.uploaded
                      ? { background: 'var(--mint-soft)', color: 'var(--green-700)', border: '1px solid var(--mint-line)' }
                      : { background: 'var(--status-warning-bg,#FBF1DE)', color: 'var(--status-warning-fg,#8A5E1D)', border: '1px solid rgba(197, 142, 58, 0.35)' }
                  }
                >
                  <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: 'currentColor' }} />
                  {row.uploaded ? t('kyc.statusUploaded') : t('kyc.statusMissing')}
                </span>
              </div>
              <div className="text-xs text-[var(--color-text-secondary)] truncate">{row.preview}</div>
            </div>
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="text-[var(--color-text-secondary)] flex-shrink-0" aria-hidden>
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}
